import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LogOut, Shield, Heart } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLink = ({ isActive }) =>
  `text-sm font-semibold tracking-wide transition-colors ${
    isActive ? "text-[#0077B6]" : "text-[#0A192F] hover:text-[#0077B6]"
  }`;

export default function Header() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/95 border-b border-[#0A192F]/5"
    >
      <div className="container-max flex items-center justify-between py-3 px-4 md:px-8">
        <Link to="/" className="flex items-center" data-testid="header-logo">
          <img src="/logo.png" alt="Midye Merkezi" className="h-20 md:h-24 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink end to="/" className={navLink} data-testid="nav-home">Anasayfa</NavLink>
          <NavLink to="/urunler" className={navLink} data-testid="nav-products">Ürünler</NavLink>
          <NavLink to="/blog" className={navLink} data-testid="nav-blog">Blog</NavLink>
          <NavLink to="/hakkimizda" className={navLink} data-testid="nav-about">Hakkımızda</NavLink>
          <NavLink to="/iletisim" className={navLink} data-testid="nav-contact">İletişim</NavLink>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Link to="/sepet" className="relative btn-ghost px-3" data-testid="cart-button">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-[#0077B6] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="btn-ghost px-3"
              >
                <User size={20} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm">
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs text-slate-500">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/hesabim" data-testid="menu-account">Hesabım</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/hesabim/favoriler" data-testid="menu-favorites">
                    <Heart size={14} className="mr-2" />Favorilerim
                  </Link>
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin">
                      <Shield size={14} className="mr-2" />Admin Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" />Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/giris" className="btn-ghost hidden md:inline-flex" data-testid="login-link">
              Giriş
            </Link>
          )}

          <button
            className="lg:hidden btn-ghost px-3"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white">
          <div className="container-max flex flex-col gap-1 p-4">
            {[
              ["Anasayfa", "/"],
              ["Ürünler", "/urunler"],
              ["Blog", "/blog"],
              ["Hakkımızda", "/hakkimizda"],
              ["İletişim", "/iletisim"],
            ].map(([label, to]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  `py-3 px-3 rounded-lg font-semibold ${
                    isActive ? "bg-[#0077B6]/10 text-[#0077B6]" : "text-[#0A192F]"
                  }`
                }
                onClick={() => setOpen(false)}
              >
                {label}
              </NavLink>
            ))}
            {!user && (
              <Link to="/giris" onClick={() => setOpen(false)} className="py-3 px-3 font-semibold text-[#0077B6]">
                Giriş / Kayıt
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
